import Button from 'components/Button'
import FormField, { FormGroup } from 'components/FormField'
import { Fragment } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { difference, dropRight } from 'lodash/fp'
import { ReactComponent as IconTrashCan } from 'assets/icons/trash-can.svg'
import Container from 'components/Container'
import {
  useAccountLanguagesQuery,
  useUpdateAccountLanguagesMutation,
} from 'generated/graphql'
import Loading from 'components/Loading'
import LANGUAGES from 'assets/data/languages.json'
import { useAuth } from 'stores/auth.store'
import { uniq } from 'lodash'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers'
import SpaceForKeyboard from 'components/SpaceForKeyboard'

type Props = {
  onComplete?: () => void
}

type FormValues = {
  languages: {
    value: string
  }[]
}

const Languages = ({ onComplete }: Props) => {
  const { account } = useAuth()
  const { data, loading, error } = useAccountLanguagesQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      accountID: account.id,
    },
  })

  if (loading) {
    return (
      <Container topBottomSpace>
        <Loading />
      </Container>
    )
  }

  if (error) {
    return <Container topBottomSpace>{error.message}</Container>
  }

  return (
    <Form
      onComplete={onComplete}
      initialState={
        data?.account_by_pk?.languages
          ? data.account_by_pk.languages
              .split(',')
              .map((v) => v.trim())
              .map((value) => ({ value }))
          : []
      }
    />
  )
}

const validationSchema = yup.object().shape({
  languages: yup.array().of(
    yup.object().shape({
      value: yup.string().required('Language is required'),
    }),
  ),
})

const Form = ({
  onComplete,
  initialState,
}: Props & {
  initialState?: FormValues['languages']
}) => {
  const { account } = useAuth()
  const [updateLanguages, updateState] = useUpdateAccountLanguagesMutation({
    onCompleted: () => {
      onComplete?.()
    },
  })
  const { control, register, formState, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      languages: initialState || [],
    },
    resolver: yupResolver(validationSchema),
  })
  const languages = useFieldArray({
    control,
    name: 'languages',
  })
  const { errors, isDirty, isSubmitting } = formState

  return (
    <Container topBottomSpace>
      <form
        onSubmit={handleSubmit(async (values) => {
          await updateLanguages({
            variables: {
              accountID: account.id,
              languages: uniq(
                values.languages.map(({ value }) => value).filter(Boolean),
              ).join(','),
            },
          })
        })}
      >
        {languages.fields.length === 0 && (
          <div className="py-2">
            Start adding languages by click on the "Add Language" button
          </div>
        )}

        {
          languages.fields.map((field, index) => (
            <Fragment key={field.id}>
              <div className="flex items-center">
                <div className="flex-grow pr-4">
                  <FormGroup>
                    <FormField
                      type="dropdown"
                      placeholder="Select a language"
                      name={`languages[${index}].value`}
                      error={errors.languages?.[index]?.value?.message}
                      register={register}
                      defaultValue={field.value}
                      control={control}
                    >
                      {difference(
                        LANGUAGES,
                        dropRight(
                          languages.fields.length - index,
                          languages.fields,
                        ).map((x: any) => x.name),
                      ).map((language) => (
                        <option value={language} key={language}>
                          {language}
                        </option>
                      ))}
                    </FormField>
                  </FormGroup>
                </div>
                <button
                  type="button"
                  className="p-2 text-center"
                  onClick={() => {
                    languages.remove(index)
                  }}
                >
                  <IconTrashCan className="inline-block w-5 fill-red-500" />
                  <span className="block body-3 text-red-500 pt-1">Remove</span>
                </button>
              </div>

              {languages.fields.length - 1 > index && (
                <hr className="border-gray-500 my-6" />
              )}
            </Fragment>
          )) as any
        }

        <FormGroup>
          {languages.fields.length < LANGUAGES.length && (
            <Button
              $fluid
              $type="secondary"
              type="button"
              onClick={() => {
                languages.append({
                  value: '',
                })
              }}
              className="mt-6"
              disabled={isSubmitting || updateState.loading}
            >
              Add language
            </Button>
          )}

          <Button
            $fluid
            disabled={!isDirty}
            loading={isSubmitting || updateState.loading}
          >
            Save Changes
          </Button>
        </FormGroup>
      </form>

      <SpaceForKeyboard $size="lg" />
    </Container>
  )
}

export default Languages
